/**
 * Máscaras de digitação, formatação para exibição e validação dos campos
 * brasileiros (CPF, telefone e CEP). Portado de
 * `primum-pipelines/src/utils/field-format.ts` — mesma regra, para os dois
 * apps concordarem sobre o que é um valor válido.
 */

export const onlyDigits = (value: string): string => value.replace(/\D/g, '');

/**
 * Máscara `000.000.000-00` que aceita entrada parcial — pode ser aplicada a
 * cada tecla sem atrapalhar quem ainda está digitando.
 */
export function formatCPF(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Formata para leitura. Só mascara o que realmente tem cara de CPF (11
 * dígitos) ou CNPJ (14) — dados legados podem trazer outro documento, e nesse
 * caso é melhor mostrar como está do que forçar um formato errado.
 */
export function formatDocumentForDisplay(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const type = detectDocumentType(value);
  if (type === 'cpf') return formatCPF(value);
  if (type === 'cnpj') return formatCNPJ(value);
  return value;
}

/** Valida os dois dígitos verificadores do CPF. */
export function isValidCPF(value: string): boolean {
  const cpf = onlyDigits(value);
  // Sequências repetidas (000.000.000-00, 111...) passam no cálculo, mas não
  // são CPFs reais.
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf[i], 10) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[9], 10)) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf[i], 10) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(cpf[10], 10);
}

/**
 * Máscara `00.000.000/0000-00` que aceita entrada parcial.
 */
export function formatCNPJ(value: string): string {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Valida os dois dígitos verificadores do CNPJ. */
export function isValidCNPJ(value: string): boolean {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;

  const calcDigit = (base: string): number => {
    const weights =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = base
      .split('')
      .reduce((acc, digit, i) => acc + parseInt(digit, 10) * weights[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const primeiroDigito = calcDigit(cnpj.slice(0, 12));
  if (primeiroDigito !== parseInt(cnpj[12], 10)) return false;

  const segundoDigito = calcDigit(cnpj.slice(0, 13));
  return segundoDigito === parseInt(cnpj[13], 10);
}

/**
 * Documento brasileiro genérico: o campo `doc` aceita tanto pessoa física
 * quanto jurídica (ex.: aluno cadastrado com CNPJ da empresa que paga a
 * matrícula) — o tipo é inferido pela quantidade de dígitos, nunca imposto.
 */
export type DocumentType = 'cpf' | 'cnpj';

/** `null` quando não dá pra dizer (nem 11 nem 14 dígitos) — não é erro, é indefinido. */
export function detectDocumentType(
  value: string | null | undefined,
): DocumentType | null {
  if (!value) return null;
  const len = onlyDigits(value).length;
  if (len === 11) return 'cpf';
  if (len === 14) return 'cnpj';
  return null;
}

/**
 * Só usado para exibição/feedback visual (bolinha verde) — nunca para
 * bloquear cadastro ou persistência. O campo `doc` sempre salva como veio,
 * inclusive de webhook/sistema externo.
 */
export function isValidDocument(value: string | null | undefined): boolean {
  const type = detectDocumentType(value);
  if (type === 'cpf') return isValidCPF(value ?? '');
  if (type === 'cnpj') return isValidCNPJ(value ?? '');
  return false;
}

/**
 * Máscara adaptativa para digitação: CPF enquanto couber em 11 dígitos,
 * CNPJ a partir do 12º — permite editar o campo `doc` sem truncar um CNPJ
 * que já esteja lá.
 */
export function formatDocumentInput(value: string): string {
  return onlyDigits(value).length > 11 ? formatCNPJ(value) : formatCPF(value);
}

// --- Telefone ---

/**
 * DDDs em uso no país. Vale a lista explícita em vez de um range: números como
 * 20, 23 ou 60 nunca foram atribuídos, e aceitá-los deixaria passar erro de
 * digitação.
 */
const VALID_DDDS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35,
  37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64,
  65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88,
  89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

/**
 * Um `+` no início marca telefone estrangeiro. A plataforma tem usuários fora
 * do Brasil (o formulário público pede código do país à parte), então aqui o
 * prefixo é o que distingue os dois casos num campo só.
 */
const isInternational = (value: string): boolean =>
  value.trim().startsWith('+');

/** Formato E.164: `+` e até 15 dígitos, sem máscara — cada país tem a sua. */
function formatInternationalPhone(value: string): string {
  return `+${onlyDigits(value).slice(0, 15)}`;
}

/**
 * Máscara `(00) 00000-0000` que aceita entrada parcial. Valores iniciados por
 * `+` passam direto como internacionais.
 */
export function formatPhone(value: string): string {
  if (isInternational(value)) return formatInternationalPhone(value);
  const d = onlyDigits(value).slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Forma canônica para persistir: só dígitos no BR, `+` mantido no
 * internacional — sem ele o número perderia o país e viraria outro telefone.
 */
export function normalizePhone(value: string): string {
  if (!value.trim()) return '';
  return isInternational(value)
    ? formatInternationalPhone(value)
    : onlyDigits(value);
}

/**
 * Formata para leitura. Só mascara o que tem cara de telefone BR (10 ou 11
 * dígitos, sem `+`); o resto sai como está.
 */
export function formatPhoneForDisplay(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (isInternational(value)) return value;
  const len = onlyDigits(value).length;
  return len === 10 || len === 11 ? formatPhone(value) : value;
}

/**
 * Telefone BR: 10 ou 11 dígitos com DDD válido; o de 11 é celular (começa com
 * 9) e o de 10 é fixo (começa com 2–5). Internacional só checa comprimento
 * plausível — não dá para validar a regra de numeração de cada país.
 */
export function isValidPhone(value: string): boolean {
  const p = onlyDigits(value);
  if (isInternational(value)) return p.length >= 8 && p.length <= 15;
  if (p.length !== 10 && p.length !== 11) return false;
  if (!VALID_DDDS.has(Number(p.slice(0, 2)))) return false;
  const firstLocal = p.charAt(2);
  if (p.length === 11) return firstLocal === '9';
  return /[2-5]/.test(firstLocal);
}

// --- CEP ---

/**
 * O campo país do endereço é texto livre, então aceitamos as grafias usuais.
 * Vazio conta como Brasil: é o default de quem preenche sem pensar no assunto,
 * e é onde a máscara ajuda.
 */
export function isBrazilianCountry(country: string): boolean {
  const c = country.trim().toLowerCase();
  return (
    c === '' || c === 'br' || c === 'bra' || c === 'brasil' || c === 'brazil'
  );
}

/** Máscara `00000-000` que aceita entrada parcial. */
export function formatCEP(value: string): string {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** CEP tem 8 dígitos e nenhum dígito verificador — só o comprimento importa. */
export function isValidCEP(value: string): boolean {
  return onlyDigits(value).length === 8;
}

/**
 * Só normaliza para dígitos o que já é numérico. Código postal estrangeiro
 * costuma ter letra (`SW1A 1AA`) e seria destruído por um `onlyDigits` cego.
 */
export function normalizeZipCode(value: string): string {
  const v = value.trim();
  return /[a-z]/i.test(v) ? v : onlyDigits(v);
}

/** Formata para leitura. Só mascara o que tem 8 dígitos e nenhuma letra. */
export function formatZipCodeForDisplay(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (/[a-z]/i.test(value)) return value;
  return isValidCEP(value) ? formatCEP(value) : value;
}
